// Store seat roster for the owner's Team & Seats screen.
// Reads are live from Firestore; every mutation goes through a callable
// because seat membership is mirrored into custom claims server-side.

import { useState, useEffect, useCallback } from 'react';
import { collection, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import type { SeatDocument, SeatRole, SeatJoinRequest } from '../types/business';

export interface PendingInvite {
  token: string;
  email: string;
  displayName: string;
  seatRole: SeatRole;
  createdAt?: any;
  expiresAt?: any;
}

export function useSeats(businessId: string | null) {
  const [seats, setSeats] = useState<SeatDocument[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [joinRequests, setJoinRequests] = useState<SeatJoinRequest[]>([]);
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    const unsubSeats = onSnapshot(
      query(collection(db, 'businesses', businessId, 'seats'), orderBy('createdAt', 'desc')),
      (snap) => {
        setSeats(snap.docs.map((d) => ({ ...(d.data() as SeatDocument), uid: d.id })));
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    const unsubInvites = onSnapshot(
      collection(db, 'businesses', businessId, 'seat_invites'),
      (snap) => setInvites(snap.docs.map((d) => ({ ...(d.data() as any), token: d.id }))),
      () => setInvites([])
    );

    const unsubRequests = onSnapshot(
      collection(db, 'businesses', businessId, 'join_requests'),
      (snap) =>
        setJoinRequests(
          snap.docs
            .map((d) => ({ ...(d.data() as SeatJoinRequest), uid: d.id }))
            .filter((r) => r.status === 'pending')
        ),
      () => setJoinRequests([])
    );

    const unsubBusiness = onSnapshot(
      doc(db, 'businesses', businessId),
      (snap) => setJoinCode(snap.data()?.joinCode || null),
      () => setJoinCode(null)
    );

    return () => {
      unsubSeats();
      unsubInvites();
      unsubRequests();
      unsubBusiness();
    };
  }, [businessId]);

  const call = useCallback(async (name: string, payload: Record<string, unknown> = {}) => {
    const fn = httpsCallable(functions, name);
    const result = await fn(payload);
    return result.data as any;
  }, []);

  const inviteSeat = useCallback(
    (email: string, displayName: string, seatRole: SeatRole) =>
      call('inviteSeat', { email, displayName, seatRole }),
    [call]
  );

  const revokeInvite = useCallback((token: string) => call('revokeSeatInvite', { token }), [call]);

  const updateSeatRole = useCallback(
    (seatUid: string, seatRole: SeatRole) => call('updateSeat', { seatUid, seatRole }),
    [call]
  );

  const setSeatStatus = useCallback(
    (seatUid: string, status: 'active' | 'suspended') => call('setSeatStatus', { seatUid, status }),
    [call]
  );

  const removeSeat = useCallback((seatUid: string) => call('removeSeat', { seatUid }), [call]);

  const resolveJoinRequest = useCallback(
    (requestUid: string, approve: boolean, seatRole: SeatRole = 'staff') =>
      call('approveSeatRequest', { requestUid, approve, seatRole }),
    [call]
  );

  const ensureJoinCode = useCallback(async () => {
    const data = await call('ensureStoreJoinCode');
    setJoinCode(data.joinCode);
    return data.joinCode as string;
  }, [call]);

  return {
    seats,
    invites,
    joinRequests,
    joinCode,
    loading,
    error,
    inviteSeat,
    revokeInvite,
    updateSeatRole,
    setSeatStatus,
    removeSeat,
    resolveJoinRequest,
    ensureJoinCode,
  };
}
